import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import { Card, CardBody, CardHeader } from "@nextui-org/react";
import { forwardRef, HTMLAttributes, ReactNode } from "react";

import { Row, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { useMediaStore } from "@/hooks";
import { getCurrentDateAndTime } from "@/utils";

import { PaymentCardHeader } from "./PaymentCardHeader";

type CellValueProps = HTMLAttributes<HTMLDivElement> & {
  label: string;
  value: ReactNode;
};

const CellValue = forwardRef<HTMLDivElement, CellValueProps>(({ label, value, children, ...props }, ref) => {
  return (
    <div ref={ref} className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-2" {...props}>
      <Text type="text-gray-700">{label}</Text>
      <Text>{value || children}</Text>
    </div>
  );
});

interface PaymentDetailsCardProps {
  currency: string;
  orderTotal: string;
  plan: string;
  date: string | null;
  order_id: string | null;
  subscription_id: string | null;
  transactionID: string | null;
}

export const PaymentDetailsCard = ({ currency, orderTotal, plan, date, order_id, subscription_id, transactionID }: PaymentDetailsCardProps) => {
  const isMobile = useMediaStore((store) => store.isMobile);
  const { t } = useTranslation();
  return (
    <Card className="w-full max-w-lg p-2 bg-transparent shadow-none gap-7">
      {!isMobile && (
        <CardHeader className="rounded-xl p-0">
          <PaymentCardHeader currency={currency} orderTotal={orderTotal} />
        </CardHeader>
      )}
      <CardBody className="flex-col gap-2 bg-gray-50 rounded-xl p-6">
        <Text variant={"regular"} type="font-[600]">
          <Trans i18nKey="subscription:checkoutSuccess.paymentDetails" />
        </Text>
        <CellValue label={t("subscription:checkoutSuccess.plan")} value={plan} />
        {!!date && <CellValue label={t("subscription:checkoutSuccess.date")} value={getCurrentDateAndTime(+date)} />}
        {!!order_id && <CellValue label={t("subscription:checkoutSuccess.orderId")} value={order_id} />}
        {!!subscription_id && <CellValue label={t("subscription:checkoutSuccess.subscriptionId")} value={subscription_id} />}
        {!!transactionID && <CellValue label={t("subscription:checkoutSuccess.transactionId")} value={transactionID} />}
        <CellValue
          label={t("subscription:checkoutSuccess.status")}
          value={
            <Row style={undefined} align={"center"} gap={0.5}>
              <Icon icon={IconVariants.CHECK} size={IconSize.SMALL} className="bg-blue-600 rounded-full p-1 shadow-[0_0_0px_10px_#0072F570]" />{" "}
              <Trans i18nKey="subscription:checkoutSuccess.success" />
            </Row>
          }
        />
      </CardBody>
    </Card>
  );
};
